"use client"

import useResize from "@/app/hooks/useResize";
import { Box, Button, colors, useTheme } from "@mui/material";
import RenderQuillContent from "@v2/components/RenderQuillContent";
import PlpSectionHeading from "./PlpSectionHeading";
import CenterBox from "@v2/components/styled/CenterBox";
import { ChevronDown } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const CLAMP_HEIGHT = 248;

// A titled section already carries an h2, so the editor's own headings have to move
// one level down to become its children instead of its siblings. Every match is read
// off the original string, so nothing is shifted twice, and h6 has nowhere to go.
// An h1 is shifted with the rest: the page already has its own, up in the header.
function shiftHeadingsDown(html: string) {
    return html.replace(/(<\/?)h([1-5])\b/gi, (_match, prefix: string, level: string) => `${prefix}h${Number(level) + 1}`);
}

export default function PlpDescription({ content, title }: { content?: string, title?: string }) {

    const theme = useTheme()
    const elementRef = useRef<HTMLDivElement>(null)
    const sectionRef = useRef<HTMLDivElement>(null)
    // Only a close the reader asked for is worth anchoring. Collapsing on a route
    // change must leave the scroll to the router.
    const anchorAfterCollapse = useRef(false)
    const pathname = usePathname()
    const [open, setOpen] = useState(false)
    const [height, setHeight] = useState<number>(0)
    const hasMoreButton = height > CLAMP_HEIGHT;

    useEffect(() => {
        setHeight(elementRef.current?.clientHeight ?? 0)
    }, [elementRef.current])

    const toggleOpen = useCallback(() => {
        setOpen(o => {
            anchorAfterCollapse.current = o
            return !o
        })
    }, [])

    // Closing takes the expanded height out of the page above the reader, so the
    // scroll position they kept now points at whatever moved up into it, which
    // reads as the page jumping to its end. Putting the section back under the
    // top of the viewport lands them where they were reading.
    useLayoutEffect(() => {
        if (open || !anchorAfterCollapse.current) return;
        anchorAfterCollapse.current = false;

        const top = sectionRef.current?.getBoundingClientRect().top;
        // A section still on screen has not lost anything above the reader.
        if (top === undefined || top >= 0) return;

        window.scrollTo(0, Math.max(0, top + window.scrollY));
    }, [open])

    const windowSize = useResize()
    useEffect(() => {
        setHeight(elementRef.current?.clientHeight ?? 0)

    }, [windowSize])
    useEffect(() => {
        setOpen(false)
        setHeight(elementRef.current?.clientHeight)
    }, [pathname])

    if (!content) return null;

    const body = title ? shiftHeadingsDown(content) : content;

    return (
        <Box component="section" ref={sectionRef} sx={{
            bgcolor: '#fff',
            width: 1

        }}>
            <Box sx={{
                maxWidth: 1400,
                marginX: 'auto',
                py: 4,
                [theme.breakpoints.down('lg2')]: {
                    px: 3
                },
            }}>
                {title && <PlpSectionHeading>{title}</PlpSectionHeading>}

                <Box sx={{
                    maxHeight: open ? 'none' : CLAMP_HEIGHT,
                    // Sticky cannot escape a clipping ancestor, so the clamp stops clipping
                    // once it is open and the close button can follow the page.
                    overflow: open ? 'visible' : 'hidden',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0, left: 0, bottom: 0,
                        height: 100,
                        // A bare `transparent` fades white towards transparent black and
                        // leaves a grey haze over the text, so the end colour is spelled out.
                        background: hasMoreButton && !open ? 'linear-gradient(0deg, #fff, rgba(255,255,255,0))' : undefined,
                    },
                }}>
                    <div ref={elementRef}>
                        <RenderQuillContent html={body} />
                        <Box sx={{ height: 100 }} />
                    </div>
                    {hasMoreButton && (
                        <CenterBox sx={{
                            py: 2,
                            width: 1,
                            position: !open ? 'absolute' : 'sticky',
                            bottom: 0,
                            zIndex: 2,
                            // While open the bar rides over the text, so it carries its own
                            // fade instead of letting words run underneath the button.
                            background: open ? 'linear-gradient(0deg, #fff 62%, rgba(255,255,255,0))' : undefined,
                        }}>
                            <Button data-nosnippet variant="outlined" color="inherit" onClick={toggleOpen} sx={{
                                gap: 1,
                                height: 48,
                                minWidth: 190,
                                px: 3,
                                bgcolor: '#fff',
                                color: colors.grey[900],
                                borderColor: colors.grey[300],
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                                '&:hover': {
                                    bgcolor: colors.grey[50],
                                    borderColor: colors.grey[900],
                                },
                            }}>
                                {open ? 'بستن' : 'مشاهده بیشتر'}
                                <ChevronDown
                                    size={18}
                                    strokeWidth={2}
                                    color={colors.grey[900]}
                                    style={{ transition: '200ms', transform: open ? 'rotate(180deg)' : undefined }}
                                />
                            </Button>
                        </CenterBox>
                    )}
                </Box>
            </Box>
        </Box>
    )
}